import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, BarChart3, Settings, Home, LogOut, Crown, 
  ChevronRight, Search, Train, Check, X, Gift, BookOpen, 
  DollarSign, Activity, Clock, Plus, Coins, UserPlus, Trash2, AlertTriangle, Key, Copy, Mail, Eye, Send, Wrench
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { apiClient, useAuth } from '../lib/api';
import { toast } from 'sonner';
import { useElapsed } from '../components/MaintenanceNotice';

const AdminSidebar = ({ currentPath }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const navItems = [
    { path: '/admin', icon: BarChart3, label: 'Dashboard' },
    { path: '/admin/users', icon: Users, label: 'Users' },
    { path: '/admin/tests', icon: Wrench, label: 'Tests' },
  ];

  return (
    <aside className="admin-sidebar w-64 flex-shrink-0 fixed left-0 top-0 h-screen overflow-y-auto">
      <div className="p-6">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 bg-white/10 rounded-sm flex items-center justify-center">
            <Train className="w-6 h-6 text-white" />
          </div>
          <div>
            <span className="font-heading text-lg font-bold text-white tracking-tight block">
              TDF Psychometrics
            </span>
            <span className="text-xs text-white/50">Admin Panel</span>
          </div>
        </Link>

        <nav className="space-y-2">
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                currentPath === item.path
                  ? 'bg-white/10 text-white'
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </nav>
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/10">
        <Link
          to="/"
          className="flex items-center gap-3 px-4 py-2 text-white/60 hover:text-white transition-colors"
        >
          <Home className="w-5 h-5" />
          Back to Site
        </Link>
        <button
          onClick={() => {
            logout();
            navigate('/');
          }}
          className="flex items-center gap-3 px-4 py-2 text-white/60 hover:text-white transition-colors w-full"
          data-testid="admin-logout"
        >
          <LogOut className="w-5 h-5" />
          Logout
        </button>
      </div>
    </aside>
  );
};

const AdminLayout = ({ children }) => {
  const location = useLocation();
  
  return (
    <div className="flex min-h-screen bg-[#F8FAFC]">
      <AdminSidebar currentPath={location.pathname} />
      <main className="flex-1 ml-64 p-8">
        {children}
      </main>
    </div>
  );
};

// Shows an admin-friendly membership summary line (source + expiry + days left).
const MembershipInfo = ({ user }) => {
  const sub = user.subscription_status;
  if (sub === 'active' || sub === 'cancelling') {
    return <div className="text-[10px] mt-1 text-green-600">{sub === 'cancelling' ? 'Subscription · cancelling' : 'Subscription · active'}</div>;
  }
  if (user.provisioned_by === 'tdf') {
    return <div className="text-[10px] mt-1 text-blue-600">Bundle / All-in-One</div>;
  }
  const exp = user.membership_expires_at;
  if (exp && user.is_member) {
    const d = new Date(exp);
    const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
    if (days > 0) {
      const cls = days > 40 ? 'text-[#0B1D51]' : 'text-amber-600';
      return <div className={`text-[10px] mt-1 ${cls}`} data-testid={`membership-expiry-${user.id}`}>Granted · until {d.toLocaleDateString('en-GB')} ({days}d left)</div>;
    }
    return <div className="text-[10px] mt-1 text-red-500" data-testid={`membership-expiry-${user.id}`}>Expired {d.toLocaleDateString('en-GB')}</div>;
  }
  if (user.access_type === 'full') {
    return <div className="text-[10px] mt-1 text-[#0B1D51]">Full access</div>;
  }
  return null;
};

export const AdminDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionHealth, setSubscriptionHealth] = useState(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [recipientType, setRecipientType] = useState('all');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientCounts, setRecipientCounts] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [isHtmlMode, setIsHtmlMode] = useState(false);

  useEffect(() => {
    Promise.all([
      apiClient.getAdminStats(),
      apiClient.getSubscriptionHealth()
    ])
      .then(([statsRes, healthRes]) => {
        setStats(statsRes.data);
        setSubscriptionHealth(healthRes.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Load recipient counts when dialog opens
  useEffect(() => {
    if (showEmailDialog && !recipientCounts) {
      apiClient.getEmailRecipientCounts()
        .then(res => setRecipientCounts(res.data))
        .catch(console.error);
    }
  }, [showEmailDialog, recipientCounts]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B1D51]" />
        </div>
      </AdminLayout>
    );
  }

  const statCards = [
    { label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: '#0B1D51' },
    { label: 'Premium Members', value: stats?.total_members || 0, icon: Crown, color: '#FF9F1C' },
    { label: 'Guide Owners', value: stats?.guide_purchases || 0, icon: BarChart3, color: '#2CB67D' },
    { label: 'Total Revenue', value: `£${stats?.total_revenue || 0}`, icon: Check, color: '#7C3AED' },
  ];

  const handlePreviewEmail = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast.error('Please fill in both subject and message');
      return;
    }
    
    setLoadingPreview(true);
    try {
      const response = await apiClient.previewEmail(emailSubject, emailMessage, 'Customer', isHtmlMode);
      setPreviewHtml(response.data.html);
      setShowPreview(true);
    } catch (error) {
      toast.error('Failed to generate preview: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast.error('Please fill in both subject and message');
      return;
    }
    
    if (recipientType === 'individual' && !recipientEmail.trim()) {
      toast.error('Please enter a recipient email address');
      return;
    }
    
    setSendingEmail(true);
    setEmailResult(null);
    
    try {
      const response = await apiClient.sendNotificationEmail(
        emailSubject, 
        emailMessage, 
        recipientType, 
        recipientType === 'individual' ? recipientEmail : null,
        false,
        isHtmlMode
      );
      setEmailResult(response.data);
      setShowPreview(false);  // Hide preview to show result
      const refreshedCounts = await apiClient.getEmailRecipientCounts();
      setRecipientCounts(refreshedCounts.data);
      toast.success(`Email sent to ${response.data.sent_count} recipient(s)`);
    } catch (error) {
      toast.error('Failed to send email: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingEmail(false);
    }
  };

  const resetEmailForm = () => {
    setEmailSubject('');
    setEmailMessage('');
    setRecipientType('all');
    setRecipientEmail('');
    setEmailResult(null);
    setShowPreview(false);
    setPreviewHtml('');
    setIsHtmlMode(false);
  };

  const getRecipientCount = () => {
    if (!recipientCounts) return '...';
    if (recipientType === 'individual') return '1';
    return recipientCounts[recipientType] || 0;
  };

  const getEmailMetrics = (category) => {
    return recipientCounts?.email_metrics?.[category] || {
      total_sent: 0,
      campaigns_sent: 0,
      last_sent_at: null,
    };
  };

  const formatLastSent = (isoDate) => {
    if (!isoDate) return 'never';
    const parsed = new Date(isoDate);
    if (Number.isNaN(parsed.getTime())) return 'never';
    return parsed.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const recipientOptions = [
    { value: 'all', label: 'All Users', description: 'Everyone who has signed up' },
    { value: 'monthly', label: 'Monthly Subscribers', description: 'Premium (£7.99/mo) plan' },
    { value: 'annual', label: 'Annual Subscribers', description: 'Complete (£29.99/yr) plan' },
    { value: 'free', label: 'Free Users', description: 'Not subscribed yet' },
    { value: 'legacy', label: 'Legacy Members', description: 'Manual/gifted access' },
    { value: 'individual', label: 'Individual', description: 'Send to specific email' },
  ];

  const trackedRecipientOptions = recipientOptions.filter((opt) => opt.value !== 'individual');

  // Pre-fill with deployment announcement
  const prefillDeploymentAnnouncement = () => {
    setEmailSubject("We've Made Some Improvements!");
    setEmailMessage(`We've been busy improving Train Driver Psychometrics based on your feedback, and we're excited to share what's new:

**More Practice Content**
• Expanded question banks across multiple tests - more variety means better preparation
• New scenarios and passages to keep your practice fresh and challenging
• Randomised answer positions - no more pattern memorisation!

**Better Test Experience**
• All tests now fully aligned with RIS-3751-TOM assessment standards
• Improved difficulty progression across all 5 levels
• Enhanced mobile experience for practice on the go

**Your Feedback Matters**
We're always looking to improve. If you notice anything that could be better, or if you have suggestions for new features, please reply to this email - we read every message.

Thank you for being part of Train Driver Psychometrics. Good luck with your train driver assessment preparation!`);
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="font-heading text-2xl font-bold text-[#0B1D51]">Admin Dashboard</h1>
        <p className="text-[#64748B]">Overview of your platform metrics</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
          >
            <Card className="border-slate-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded-sm flex items-center justify-center"
                    style={{ backgroundColor: `${stat.color}10` }}
                  >
                    <stat.icon className="w-6 h-6" style={{ color: stat.color }} />
                  </div>
                  <div>
                    <p className="text-sm text-[#64748B]">{stat.label}</p>
                    <p className="font-mono text-2xl font-bold text-[#0B1D51]">{stat.value}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-[#0B1D51]">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/admin/users">
              <Button variant="outline" className="w-full justify-between">
                Manage Users
                <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/tests">
              <Button variant="outline" className="w-full justify-between">
                View Tests
                <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
            <Button 
              variant="outline" 
              className="w-full justify-between border-[#FF9F1C] text-[#FF9F1C] hover:bg-[#FF9F1C]/10"
              onClick={() => {
                resetEmailForm();
                setShowEmailDialog(true);
              }}
              data-testid="send-notification-email-btn"
            >
              <span className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Send Notification Email
              </span>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>

        {/* Email Notification Dialog */}
        <Dialog open={showEmailDialog} onOpenChange={(open) => {
          setShowEmailDialog(open);
          if (!open) {
            resetEmailForm();
          }
        }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="email-notification-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#0B1D51]">
                <Mail className="w-5 h-5 text-[#FF9F1C]" />
                Send Notification Email
              </DialogTitle>
              <DialogDescription>
                Send branded emails to individuals, groups, or all users.
              </DialogDescription>
            </DialogHeader>
            
            {showPreview ? (
              <div className="space-y-4 py-4" data-testid="email-preview-container">
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-slate-100 px-4 py-2 border-b flex items-center justify-between">
                    <span className="text-sm font-medium text-[#64748B]">Email Preview</span>
                    <Badge variant="outline" data-testid="email-preview-subject">Subject: {emailSubject}</Badge>
                  </div>
                  <div 
                    className="p-4 bg-white"
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                    data-testid="email-preview-html"
                  />
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setShowPreview(false)} className="flex-1" data-testid="email-back-to-edit-btn">
                    Back to Edit
                  </Button>
                  <Button 
                    onClick={handleSendEmail}
                    disabled={sendingEmail}
                    className="flex-1 bg-[#FF9F1C] hover:bg-[#e8900a]"
                    data-testid="email-send-btn"
                  >
                    {sendingEmail ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send to {getRecipientCount()} Recipient(s)
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : !emailResult ? (
              <div className="space-y-4 py-4">
                {/* Recipient Selection */}
                <div>
                  <Label className="mb-2 block">Send To</Label>
                  <Select value={recipientType} onValueChange={setRecipientType}>
                    <SelectTrigger className="w-full" data-testid="recipient-type-select">
                      <SelectValue placeholder="Select recipients" />
                    </SelectTrigger>
                    <SelectContent data-testid="recipient-type-options">
                      {recipientOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value} data-testid={`recipient-option-${opt.value}`}>
                          <div className="flex items-center justify-between w-full">
                            <span>{opt.label}</span>
                            <span className="text-xs text-[#64748B] ml-2">
                              {opt.value !== 'individual' && recipientCounts
                                ? `${recipientCounts[opt.value] || 0} users | ${getEmailMetrics(opt.value).total_sent} sent | last ${formatLastSent(getEmailMetrics(opt.value).last_sent_at)}`
                                : opt.description
                              }
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {recipientType === 'individual' && (
                    <Input
                      type="email"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      placeholder="Enter email address"
                      className="mt-2"
                      data-testid="individual-email-input"
                    />
                  )}
                  
                  <p className="text-xs text-[#64748B] mt-1">
                    {recipientType !== 'individual' && recipientCounts && (
                      <>Will send to <strong>{getRecipientCount()}</strong> recipient(s) who haven&apos;t unsubscribed</>
                    )}
                  </p>
                  {recipientType !== 'individual' && recipientCounts && (
                    <p className="text-xs text-[#64748B] mt-1">
                      Emails sent to this category: <strong>{getEmailMetrics(recipientType).total_sent}</strong> | Last sent: <strong>{formatLastSent(getEmailMetrics(recipientType).last_sent_at)}</strong>
                    </p>
                  )}
                  {recipientCounts && (
                    <div className="mt-3 border rounded-md overflow-hidden" data-testid="recipient-history-table">
                      <div className="px-3 py-2 bg-slate-50 border-b">
                        <p className="text-xs font-medium text-[#0B1D51]">Recipient Category Email History</p>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-xs">
                          <thead className="bg-white text-[#64748B]">
                            <tr>
                              <th className="text-left px-3 py-2 font-medium">Category</th>
                              <th className="text-right px-3 py-2 font-medium">Recipients</th>
                              <th className="text-right px-3 py-2 font-medium">Emails Sent</th>
                              <th className="text-right px-3 py-2 font-medium">Campaigns</th>
                              <th className="text-left px-3 py-2 font-medium">Last Sent</th>
                            </tr>
                          </thead>
                          <tbody>
                            {trackedRecipientOptions.map((opt) => {
                              const metrics = getEmailMetrics(opt.value);
                              return (
                                <tr key={`recipient-history-${opt.value}`} className="border-t">
                                  <td className="px-3 py-2 text-[#0B1D51] font-medium">{opt.label}</td>
                                  <td className="px-3 py-2 text-right text-[#0B1D51]">{recipientCounts[opt.value] || 0}</td>
                                  <td className="px-3 py-2 text-right text-[#0B1D51]">{metrics.total_sent}</td>
                                  <td className="px-3 py-2 text-right text-[#0B1D51]">{metrics.campaigns_sent}</td>
                                  <td className="px-3 py-2 text-[#64748B]">{formatLastSent(metrics.last_sent_at)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Subject */}
                <div>
                  <Label htmlFor="email-subject">Subject</Label>
                  <Input
                    id="email-subject"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="e.g., Important Update from Train Driver Psychometrics"
                    className="mt-1"
                    data-testid="email-subject-input"
                  />
                </div>
                
                {/* Message */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="email-message">Message</Label>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          id="html-mode"
                          checked={isHtmlMode}
                          onCheckedChange={setIsHtmlMode}
                          data-testid="html-mode-toggle"
                        />
                        <Label htmlFor="html-mode" className="text-xs text-[#64748B] cursor-pointer">
                          HTML Mode
                        </Label>
                      </div>
                      {!isHtmlMode && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-xs text-[#64748B]"
                          onClick={prefillDeploymentAnnouncement}
                        >
                          Use Template
                        </Button>
                      )}
                    </div>
                  </div>
                  <Textarea
                    id="email-message"
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    placeholder={isHtmlMode 
                      ? "Paste your HTML content here. Example:\n<h1 style=\"color: #0B1D51;\">Hello!</h1>\n<p>Your message here...</p>" 
                      : "Write your message here. Use **text** for bold, and start lines with • for bullet points."
                    }
                    className={`mt-1 min-h-[200px] font-mono text-sm ${isHtmlMode ? 'bg-slate-50' : ''}`}
                    data-testid="email-message-input"
                  />
                  {isHtmlMode && (
                    <p className="text-xs text-amber-600 mt-1">
                      HTML mode: Your content will be sent as raw HTML. No template wrapper will be applied.
                    </p>
                  )}
                </div>
                
                {/* Warning */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-amber-800 text-sm flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Preview your email before sending. All emails include an unsubscribe link.
                    </span>
                  </p>
                </div>
              </div>
            ) : (
              <div className="py-6" data-testid="email-result-container">
                <div className={`text-center p-6 rounded-lg ${emailResult.sent_count > 0 ? 'bg-green-50' : 'bg-red-50'}`} data-testid="email-result-status">
                  {emailResult.sent_count > 0 ? (
                    <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  ) : (
                    <X className="w-12 h-12 text-red-500 mx-auto mb-3" />
                  )}
                  <h3 className="font-semibold text-lg text-[#0B1D51] mb-2" data-testid="email-result-title">
                    {emailResult.sent_count > 0 ? 'Email Sent!' : 'Send Failed'}
                  </h3>
                  <p className="text-[#64748B]" data-testid="email-result-message">
                    Successfully sent to <strong>{emailResult.sent_count}</strong> recipient(s)
                    {emailResult.failed_count > 0 && (
                      <span className="text-red-600"> ({emailResult.failed_count} failed)</span>
                    )}
                  </p>
                </div>
              </div>
            )}
            
            <DialogFooter>
              {showPreview ? null : !emailResult ? (
                <>
                  <Button variant="outline" onClick={() => setShowEmailDialog(false)} data-testid="email-cancel-btn">
                    Cancel
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={handlePreviewEmail}
                    disabled={loadingPreview || !emailSubject.trim() || !emailMessage.trim()}
                    data-testid="email-preview-btn"
                  >
                    {loadingPreview ? (
                      <span className="animate-spin mr-2">⏳</span>
                    ) : (
                      <Eye className="w-4 h-4 mr-2" />
                    )}
                    Preview
                  </Button>
                </>
              ) : (
                <Button onClick={() => {
                  resetEmailForm();
                  setShowEmailDialog(false);
                }} data-testid="email-close-btn">
                  Close
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-[#0B1D51]">Platform Health</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[#64748B]">API Status</span>
                <Badge className="bg-[#2CB67D]">Operational</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[#64748B]">Database</span>
                <Badge className="bg-[#2CB67D]">Connected</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[#64748B]">Payment Gateway</span>
                <Badge className="bg-[#2CB67D]">Active</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Subscription Health Section */}
      {subscriptionHealth && (
        <div className="mt-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-[#0B1D51] flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Subscription Health
              </CardTitle>
              <CardDescription>Monitor subscription status and payment failures</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{subscriptionHealth.subscription_counts?.active || 0}</div>
                  <div className="text-xs text-green-700">Active</div>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <div className="text-2xl font-bold text-amber-600">{subscriptionHealth.subscription_counts?.cancelling || 0}</div>
                  <div className="text-xs text-amber-700">Cancelling</div>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">{subscriptionHealth.subscription_counts?.past_due || 0}</div>
                  <div className="text-xs text-orange-700">Past Due</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{subscriptionHealth.subscription_counts?.revoked || 0}</div>
                  <div className="text-xs text-red-700">Revoked</div>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <div className="text-2xl font-bold text-slate-600">{subscriptionHealth.subscription_counts?.cancelled || 0}</div>
                  <div className="text-xs text-slate-700">Cancelled</div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-sm text-[#0B1D51] mb-2">Last 30 Days</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#64748B]">Payment Failures</span>
                      <span className={subscriptionHealth.last_30_days?.payment_failures > 0 ? 'text-red-600 font-semibold' : 'text-slate-600'}>
                        {subscriptionHealth.last_30_days?.payment_failures || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#64748B]">Revocations</span>
                      <span className={subscriptionHealth.last_30_days?.revocations > 0 ? 'text-red-600 font-semibold' : 'text-slate-600'}>
                        {subscriptionHealth.last_30_days?.revocations || 0}
                      </span>
                    </div>
                  </div>
                </div>

                {subscriptionHealth.at_risk_users?.length > 0 && (
                  <div className="border border-orange-200 bg-orange-50 rounded-lg p-4">
                    <h4 className="font-semibold text-sm text-orange-800 mb-2 flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />
                      At-Risk Users ({subscriptionHealth.at_risk_users.length})
                    </h4>
                    <div className="space-y-1 text-sm max-h-32 overflow-y-auto">
                      {subscriptionHealth.at_risk_users.slice(0, 5).map((user, i) => (
                        <div key={i} className="flex justify-between text-orange-700">
                          <span className="truncate">{user.email}</span>
                          <span className="text-xs">
                            {user.payment_failure_count || 1} failed
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
};

export const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showCreditsDialog, setShowCreditsDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', is_member: false, free_credits: 3 });
  const [creditsAmount, setCreditsAmount] = useState(3);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showPasswordResetDialog, setShowPasswordResetDialog] = useState(false);
  const [passwordResetUser, setPasswordResetUser] = useState(null);
  const [resetResult, setResetResult] = useState(null);

  const fetchUsers = async () => {
    try {
      const res = await apiClient.searchAdminUsers({});
      setUsers(res.data);
    } catch (error) {
      // Fallback to old endpoint
      const res = await apiClient.getAdminUsers();
      setUsers(res.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.email || !newUser.name || !newUser.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setActionLoading('create-user');
    try {
      await apiClient.createUser(newUser);
      toast.success(`User ${newUser.email} created successfully`);
      setShowAddUserDialog(false);
      setNewUser({ name: '', email: '', password: '', is_member: false, free_credits: 3 });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
    setActionLoading(null);
  };

  const handleSetCredits = async () => {
    if (!selectedUser) return;
    
    setActionLoading(`credits-${selectedUser.id}`);
    try {
      await apiClient.setUserCredits(selectedUser.id, creditsAmount);
      setUsers(users.map(u => 
        u.id === selectedUser.id ? { ...u, free_credits: creditsAmount } : u
      ));
      toast.success(`Set ${creditsAmount} free credits for ${selectedUser.email}`);
      setShowCreditsDialog(false);
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set credits');
    }
    setActionLoading(null);
  };

  const handleAddCredits = async (userId, email, amount = 1) => {
    setActionLoading(`add-credits-${userId}`);
    try {
      const res = await apiClient.addUserCredits(userId, amount);
      setUsers(users.map(u => 
        u.id === userId ? { ...u, free_credits: res.data.free_credits } : u
      ));
      toast.success(`Added ${amount} credit(s) to ${email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add credits');
    }
    setActionLoading(null);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setActionLoading(`delete-${userToDelete.id}`);
    try {
      await apiClient.deleteUser(userToDelete.id);
      setUsers(users.filter(u => u.id !== userToDelete.id));
      toast.success(`User ${userToDelete.email} has been deleted`);
      setShowDeleteDialog(false);
      setUserToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
    setActionLoading(null);
  };

  const handleResetPassword = async (sendEmail = true) => {
    if (!passwordResetUser) return;
    
    setActionLoading(`reset-pw-${passwordResetUser.id}`);
    try {
      const res = await apiClient.adminResetPassword(passwordResetUser.id, sendEmail);
      setResetResult({
        tempPassword: res.data.temp_password,
        emailSent: res.data.email_sent,
        email: passwordResetUser.email
      });
      toast.success(`Password reset for ${passwordResetUser.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
      setShowPasswordResetDialog(false);
      setPasswordResetUser(null);
    }
    setActionLoading(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleToggleMember = async (userId, currentStatus) => {
    try {
      await apiClient.updateAdminUser(userId, { is_member: !currentStatus });
      setUsers(users.map(u => 
        u.id === userId ? { ...u, is_member: !currentStatus } : u
      ));
      toast.success('User updated successfully');
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleToggleAdmin = async (userId, currentStatus) => {
    try {
      await apiClient.updateAdminUser(userId, { is_admin: !currentStatus });
      setUsers(users.map(u => 
        u.id === userId ? { ...u, is_admin: !currentStatus } : u
      ));
      toast.success('User updated successfully');
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleGrantGuide = async (userId, email) => {
    setActionLoading(`guide-${userId}`);
    try {
      await apiClient.grantUserGuide(userId);
      setUsers(users.map(u => 
        u.id === userId ? { ...u, has_guide: true } : u
      ));
      toast.success(`Guide access granted to ${email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant guide');
    }
    setActionLoading(null);
  };

  const handleGrantMembership = async (userId, email, days = 30) => {
    setActionLoading(`member-${days}-${userId}`);
    try {
      await apiClient.grantUserMembership(userId, days);
      const future = new Date(Date.now() + days * 86400000).toISOString();
      setUsers(users.map(u => 
        u.id === userId ? { ...u, is_member: true, membership_expires_at: future } : u
      ));
      const label = days === 365 ? '1-year full access' : `${days}-day membership`;
      toast.success(`${label} granted to ${email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant membership');
    }
    setActionLoading(null);
  };

  const handleRevokeMembership = async (userId, email) => {
    setActionLoading(`revoke-${userId}`);
    try {
      await apiClient.revokeUserMembership(userId);
      setUsers(users.map(u => 
        u.id === userId ? { ...u, is_member: false } : u
      ));
      toast.success(`Membership revoked from ${email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke membership');
    }
    setActionLoading(null);
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#0B1D51]">User Management</h1>
          <p className="text-[#64748B]">Manage user accounts, memberships, and free credits</p>
        </div>
        <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
          <DialogTrigger asChild>
            <Button className="bg-[#0B1D51]" data-testid="add-user-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Add New User
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
              <DialogDescription>
                Add a new member directly to the platform.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser}>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={newUser.name}
                    onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                    placeholder="John Smith"
                    data-testid="new-user-name"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    placeholder="john@example.com"
                    data-testid="new-user-email"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    placeholder="Min 6 characters"
                    data-testid="new-user-password"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="credits">Free Credits</Label>
                  <Input
                    id="credits"
                    type="number"
                    min="0"
                    max="100"
                    value={newUser.free_credits}
                    onChange={(e) => setNewUser({...newUser, free_credits: parseInt(e.target.value) || 0})}
                    data-testid="new-user-credits"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="is_member"
                    checked={newUser.is_member}
                    onCheckedChange={(checked) => setNewUser({...newUser, is_member: checked})}
                    data-testid="new-user-member"
                  />
                  <Label htmlFor="is_member">Grant 30-day membership</Label>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAddUserDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={actionLoading === 'create-user'} data-testid="create-user-submit">
                  {actionLoading === 'create-user' ? 'Creating...' : 'Create User'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Set Credits Dialog */}
      <Dialog open={showCreditsDialog} onOpenChange={setShowCreditsDialog}>
        <DialogContent className="sm:max-w-[350px]">
          <DialogHeader>
            <DialogTitle>Set Free Credits</DialogTitle>
            <DialogDescription>
              Set the number of free test attempts for {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="grid gap-2">
              <Label htmlFor="credits-amount">Number of Credits</Label>
              <Input
                id="credits-amount"
                type="number"
                min="0"
                max="100"
                value={creditsAmount}
                onChange={(e) => setCreditsAmount(parseInt(e.target.value) || 0)}
                data-testid="set-credits-input"
              />
              <p className="text-xs text-slate-500">
                Current: {selectedUser?.free_credits || 0} credits
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreditsDialog(false)}>Cancel</Button>
            <Button onClick={handleSetCredits} disabled={actionLoading?.startsWith('credits-')} data-testid="set-credits-submit">
              Set Credits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete User Account
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{userToDelete?.email}</strong>? 
              This action cannot be undone. All user data including scores and purchases will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setUserToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
              disabled={actionLoading?.startsWith('delete-')}
            >
              {actionLoading?.startsWith('delete-') ? 'Deleting...' : 'Delete User'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordResetDialog} onOpenChange={(open) => {
        if (!open) {
          setShowPasswordResetDialog(false);
          setPasswordResetUser(null);
          setResetResult(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-500" />
              Reset User Password
            </DialogTitle>
            <DialogDescription>
              {resetResult ? (
                <span>Password has been reset for <strong>{resetResult.email}</strong></span>
              ) : (
                <span>Generate a new password for <strong>{passwordResetUser?.email}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {resetResult ? (
            <div className="space-y-4">
              <div className="bg-slate-100 rounded-lg p-4">
                <Label className="text-sm text-slate-500 mb-2 block">Temporary Password</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white border rounded px-3 py-2 font-mono text-lg">
                    {resetResult.tempPassword}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(resetResult.tempPassword)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className={`flex items-center gap-2 text-sm ${resetResult.emailSent ? 'text-green-600' : 'text-amber-600'}`}>
                <Mail className="w-4 h-4" />
                {resetResult.emailSent 
                  ? 'Email sent to user with new password' 
                  : 'Email not sent - share password manually'}
              </div>
              
              <p className="text-sm text-slate-500">
                Please share this password with the user securely. They should change it after logging in.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                This will generate a random temporary password and can optionally email it to the user.
              </p>
              <div className="flex items-center gap-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <p className="text-sm text-amber-700">
                  Note: If email delivery fails (e.g., Hotmail blocking), you can still copy the password and share it manually.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            {resetResult ? (
              <Button onClick={() => {
                setShowPasswordResetDialog(false);
                setPasswordResetUser(null);
                setResetResult(null);
              }}>
                Done
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPasswordResetDialog(false);
                    setPasswordResetUser(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleResetPassword(true)}
                  disabled={actionLoading?.startsWith('reset-pw-')}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {actionLoading?.startsWith('reset-pw-') ? 'Resetting...' : 'Reset & Email Password'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
              <Input
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-users"
              />
            </div>
            <Badge variant="outline" className="text-[#64748B]">
              {filteredUsers.length} users
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B1D51]" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Last Active</TableHead>
                  <TableHead>Credits</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map(user => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#0B1D51] rounded-full flex items-center justify-center text-white text-sm font-medium">
                          {user.name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <span className="font-medium">{user.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-[#64748B]">{user.email}</TableCell>
                    <TableCell className="text-[#64748B]">
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-[#64748B]">
                      {user.last_test_date ? (
                        <div className="text-xs">
                          <div>{new Date(user.last_test_date).toLocaleDateString()}</div>
                          <div className="text-[#94A3B8] capitalize">{user.last_test_slug?.replace(/-/g, ' ') || ''}</div>
                        </div>
                      ) : (
                        <span className="text-[#94A3B8] italic text-xs">Never</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="font-mono">
                          <Coins className="w-3 h-3 mr-1 text-amber-500" />
                          {user.free_credits || 0}
                        </Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={() => handleAddCredits(user.id, user.email, 1)}
                          disabled={actionLoading === `add-credits-${user.id}`}
                          title="Add 1 credit"
                          data-testid={`add-credit-${user.id}`}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs"
                          onClick={() => {
                            setSelectedUser(user);
                            setCreditsAmount(user.free_credits || 0);
                            setShowCreditsDialog(true);
                          }}
                          title="Set credits"
                          data-testid={`set-credits-${user.id}`}
                        >
                          Edit
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {user.is_admin && (
                          <Badge className="bg-purple-100 text-purple-700 text-xs">Admin</Badge>
                        )}
                        {user.provisioned_by === 'tdf' && (
                          <Badge className="bg-blue-100 text-blue-700 text-xs">TDF</Badge>
                        )}
                        {user.is_member && (
                          <Badge className="bg-[#FF9F1C]/10 text-[#FF9F1C] text-xs">
                            <Crown className="w-3 h-3 mr-1" />
                            Member
                          </Badge>
                        )}
                        {user.has_guide && (
                          <Badge className="bg-[#2CB67D]/10 text-[#2CB67D] text-xs">
                            <BookOpen className="w-3 h-3 mr-1" />
                            Guide
                          </Badge>
                        )}
                        {!user.is_member && !user.has_guide && (
                          <Badge variant="outline" className="text-xs">Free</Badge>
                        )}
                      </div>
                      <MembershipInfo user={user} />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs"
                          onClick={() => handleGrantMembership(user.id, user.email, 30)}
                          disabled={actionLoading === `member-30-${user.id}`}
                          data-testid={`grant-30d-${user.id}`}
                          title={user.is_member ? 'Downgrade to 30-day access' : 'Grant 30-day access'}
                        >
                          <Gift className="w-3 h-3 mr-1" />
                          {user.is_member ? 'Set 30d' : 'Grant 30d'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs text-[#0B1D51] border-[#0B1D51]/30 hover:bg-[#0B1D51]/5"
                          onClick={() => handleGrantMembership(user.id, user.email, 365)}
                          disabled={actionLoading === `member-365-${user.id}`}
                          data-testid={`grant-1yr-${user.id}`}
                          title="Grant 1-year full access"
                        >
                          <Crown className="w-3 h-3 mr-1" />
                          Grant 1yr
                        </Button>
                        {user.is_member && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs text-red-600 hover:bg-red-50"
                            onClick={() => handleRevokeMembership(user.id, user.email)}
                            disabled={actionLoading === `revoke-${user.id}`}
                            data-testid={`revoke-membership-${user.id}`}
                            title="Revoke membership (back to free)"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Revoke
                          </Button>
                        )}
                        {!user.has_guide && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            onClick={() => handleGrantGuide(user.id, user.email)}
                            disabled={actionLoading === `guide-${user.id}`}
                          >
                            <BookOpen className="w-3 h-3 mr-1" />
                            Grant Guide
                          </Button>
                        )}
                        <Switch
                          checked={user.is_admin}
                          onCheckedChange={() => handleToggleAdmin(user.id, user.is_admin)}
                          data-testid={`toggle-admin-${user.id}`}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-xs text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                          onClick={() => {
                            setPasswordResetUser(user);
                            setResetResult(null);
                            setShowPasswordResetDialog(true);
                          }}
                          title="Reset password"
                          data-testid={`reset-pw-${user.id}`}
                        >
                          <Key className="w-3 h-3" />
                        </Button>
                        {!user.is_admin && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                            onClick={() => {
                              setUserToDelete(user);
                              setShowDeleteDialog(true);
                            }}
                            title="Delete user"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
};


// ============ ADMIN: TEST MAINTENANCE ============
const MaintenanceRow = ({ test, onToggle, onSaveMessage, busy }) => {
  const elapsed = useElapsed(test.is_maintenance ? test.started_at : null);
  const [msg, setMsg] = useState(test.message || '');

  useEffect(() => {
    setMsg(test.message || '');
  }, [test.message]);

  return (
    <TableRow data-testid={`maintenance-row-${test.slug}`} className={test.is_maintenance ? 'bg-amber-50/60' : ''}>
      <TableCell>
        <div className="font-medium text-[#0B1D51]">{test.name}</div>
        <div className="text-xs text-[#64748B]">{test.slug}</div>
      </TableCell>
      <TableCell>
        {test.is_maintenance ? (
          <Badge className="bg-amber-100 text-amber-800 border border-amber-300 gap-1">
            <Wrench className="w-3 h-3" /> Under Maintenance
          </Badge>
        ) : (
          <Badge className="bg-green-100 text-green-700 border border-green-300 gap-1">
            <Check className="w-3 h-3" /> Live
          </Badge>
        )}
      </TableCell>
      <TableCell>
        {test.is_maintenance && elapsed ? (
          <span className="font-mono text-sm font-semibold text-amber-900 tabular-nums" data-testid={`maintenance-elapsed-${test.slug}`}>
            {elapsed}
          </span>
        ) : (
          <span className="text-[#94A3B8] text-sm">—</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-3">
          <Switch
            checked={test.is_maintenance}
            disabled={busy}
            onCheckedChange={(checked) => onToggle(test, checked, msg)}
            data-testid={`maintenance-toggle-${test.slug}`}
          />
          {test.is_maintenance && (
            <div className="flex items-center gap-2 flex-1">
              <Input
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                placeholder="Custom message (optional)"
                className="h-9 text-sm w-64"
                data-testid={`maintenance-message-input-${test.slug}`}
              />
              <Button
                size="sm"
                variant="outline"
                disabled={busy || msg === (test.message || '')}
                onClick={() => onSaveMessage(test, msg)}
                data-testid={`maintenance-save-message-${test.slug}`}
              >
                Save
              </Button>
            </div>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

export const AdminTestsPage = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busySlug, setBusySlug] = useState(null);

  const load = async () => {
    try {
      const res = await apiClient.getTestMaintenance();
      setTests(res.data);
    } catch (e) {
      toast.error('Failed to load tests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleToggle = async (test, enabled, message) => {
    setBusySlug(test.slug);
    try {
      await apiClient.setTestMaintenance(test.slug, enabled, enabled ? message : null);
      toast.success(enabled ? `${test.name} is now under maintenance` : `${test.name} is back live`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update maintenance');
    } finally {
      setBusySlug(null);
    }
  };

  const handleSaveMessage = async (test, message) => {
    setBusySlug(test.slug);
    try {
      await apiClient.setTestMaintenance(test.slug, true, message);
      toast.success('Message updated');
      await load();
    } catch (e) {
      toast.error('Failed to update message');
    } finally {
      setBusySlug(null);
    }
  };

  const activeCount = tests.filter(t => t.is_maintenance).length;

  return (
    <AdminLayout>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#0B1D51]">Test Maintenance</h1>
          <p className="text-[#64748B] text-sm mt-1">
            Temporarily take individual tests offline. All users will see a "back soon" message with a live timer.
          </p>
        </div>
        {activeCount > 0 && (
          <Badge className="bg-amber-100 text-amber-800 border border-amber-300 gap-1" data-testid="maintenance-active-count">
            <Wrench className="w-3 h-3" /> {activeCount} under maintenance
          </Badge>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">All Tests</CardTitle>
          <CardDescription>Toggle a test to instantly put it into (or take it out of) maintenance mode.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 flex justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0B1D51]" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Test</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Elapsed</TableHead>
                  <TableHead>Maintenance Control</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tests.map((test) => (
                  <MaintenanceRow
                    key={test.slug}
                    test={test}
                    busy={busySlug === test.slug}
                    onToggle={handleToggle}
                    onSaveMessage={handleSaveMessage}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
};
